/**
 * `location` — an address, optionally with coordinates.
 *
 * **No geocoding provider is wired** (the brief: none for now), so this is a free-text address
 * plus an optional lat/lng pair — which is exactly what monday itself stores when it has no
 * Place ID: *"Without a Google Place ID only `lat`, `lng`, `address` populate."* `[09§A.7]`.
 * A map preview is an open question (q336), so the cell shows the address and, when the cell
 * has coordinates, a link out to a map rather than an embedded one we cannot license.
 */
import { getColumnType, type LocationSettings, type LocationValue } from '@elizmonday/shared';
import { CellPopover, useEditorDraft } from '../primitives';
import { defineColumnUi } from '../types';
import { sharedBuckets } from '../buckets';
import { LocationSettingsDialog } from './SettingsDialog';

const shared = getColumnType('location');

/** Measured: monday's own address box is 246 × 48 (`editors/location/tier2-capture.json`). */
export const LOCATION_EDITOR_WIDTH = 300;

export const locationColumnUi = defineColumnUi<LocationValue, LocationSettings>({
  type: 'location',
  icon: 'columnGeneric',
  gallery: { group: 'advanced', keywords: ['location', 'address', 'map', 'place'] },
  /** P-022: the registry default. ASSUMED — no `width: null` column of this type is measured. */
  defaultWidth: 140,
  align: 'left',
  groupable: true,

  Cell: ({ value, context }) => {
    if (value === null) return <span className="ez-cell__empty" aria-hidden="true" />;
    return (
      <span className="ez-cell__text" data-align="left" title={value.address}>
        {value.address}
        {context.search === '' && value.lat && value.lng ? (
          <span className="ez-location__pin" aria-hidden="true">
            📍
          </span>
        ) : null}
      </span>
    );
  },

  /** **P-282**: the three fields are one draft that follows the store while untouched, and an
   * untouched dismiss writes nothing — see `useEditorDraft`. */
  Editor: ({ value, initialInput, onCommit, onCancel }) => {
    const stored = { address: value?.address ?? '', lat: value?.lat ?? '', lng: value?.lng ?? '' };
    const editor = useEditorDraft(
      stored,
      initialInput === undefined ? undefined : { address: initialInput, lat: '', lng: '' },
    );
    const { address, lat, lng } = editor.draft;
    const setAddress = (next: string): void => editor.set({ address: next, lat, lng });
    const setLat = (next: string): void => editor.set({ address, lat: next, lng });
    const setLng = (next: string): void => editor.set({ address, lat, lng: next });
    const commit = (): void => {
      const trimmed = address.trim();
      if (trimmed === '') {
        onCommit(null);
        return;
      }
      const next: LocationValue = { address: trimmed };
      if (lat.trim() !== '') next.lat = lat.trim();
      if (lng.trim() !== '') next.lng = lng.trim();
      onCommit(next);
    };
    const dismiss = (): void => editor.dismiss(commit, onCancel);
    return (
      <CellPopover label="Edit location" width={LOCATION_EDITOR_WIDTH} onDismiss={dismiss} onCancel={onCancel}>
        <div className="ez-two-field">
          <label className="ez-two-field__label" htmlFor="ez-location-address">
            Address
          </label>
          <input
            id="ez-location-address"
            className="ez-two-field__input"
            data-testid="location-address"
            autoFocus
            value={address}
            placeholder="Type an address"
            onChange={(event) => setAddress(event.target.value)}
            onKeyDown={(event) => {
              event.stopPropagation();
              if (event.key === 'Enter') commit();
              if (event.key === 'Escape') onCancel();
            }}
          />
          <div className="ez-two-field__pair">
            <input
              className="ez-two-field__input"
              aria-label="Latitude"
              data-testid="location-lat"
              value={lat}
              placeholder="Latitude"
              onChange={(event) => setLat(event.target.value)}
              onKeyDown={(event) => event.stopPropagation()}
            />
            <input
              className="ez-two-field__input"
              aria-label="Longitude"
              data-testid="location-lng"
              value={lng}
              placeholder="Longitude"
              onChange={(event) => setLng(event.target.value)}
              onKeyDown={(event) => event.stopPropagation()}
            />
          </div>
          {/* No provider is configured, so we say so instead of pretending to search (q336). */}
          <p className="ez-two-field__hint">Address search is not configured; type the address.</p>
        </div>
      </CellPopover>
    );
  },

  SettingsDialog: LocationSettingsDialog,

  fromClipboard(text, settings) {
    const trimmed = text.trim();
    return trimmed === '' ? null : (shared.parseValue({ address: trimmed }, settings) as LocationValue | null);
  },
  toClipboard(value, settings) {
    return shared.toText(value, settings) ?? '';
  },
  bucketsOf: sharedBuckets('location'),
});
